import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PageContainer, ProDescriptions } from '@ant-design/pro-components';
import Layouts from '../../components/Layouts';
import { useDispatch } from 'react-redux';
import { createCity, deleteCity, getCity, searchCity, searchCountry, updateCity } from '../../store/bids';
import socket from '../../socket';
import './location.scss';
import CityRow from './CityRow';
import { AutoComplete, Button, Col, Flex, Form, Input, message, Modal, Row } from 'antd';
import { useTranslation } from 'react-i18next';


export interface City {
  id: number;
  key: string;
  title: string;
  country: string;
  dataIndex: string;
  similar: boolean;
  duplicate: boolean;
  city: string;
}

const City: React.FC = () => {
  const actionRef = useRef<any>();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [cities, setCities] = useState<City[]>([]);
  const [optionCities, setOptionCities] = useState<City[]>([]);
  const [optionCountry, setOptionCountry] = useState<City[]>([]);
  const [editingCityKey, setEditingCityKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [inputValueOld, setInputValueOld] = useState<string>('');
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [isOpenRefCity, setIsOpenRefCity] = useState(false);
  const [arCity, setArCity] = useState<string[]>(() => {
    // Инициализация состояния из sessionStorage, если есть
    const savedCities = sessionStorage.getItem('city');
    return savedCities ? JSON.parse(savedCities) : [];
  });
  const [langs, setLangs] = useState<string>();
  const locale: any = localStorage.getItem('i18nextLng');


  useEffect(() => {
    socket.on('return-get-city', handleCity);
    socket.on('return-update-city', handleCityUpdate);
    socket.on('return-search-city', resSearchCity);
    socket.on('return-search-country', resSearchCountry);
    socket.on('return-create-city', resCreateCity);
    socket.on('return-delete-city', resDeleteCity);

    if (locale) {
      setLangs(locale);
    } else {
      setLangs(locale);
    }
    i18n.changeLanguage(langs).then((r) => {
      dispatch(getCity({locale: locale}));
    });
    return () => {
      socket.off('return-get-city', handleCity);
      socket.off('return-update-city', handleCityUpdate);
      socket.off('return-search-city', resSearchCity);
      socket.off('return-search-country', resSearchCountry);
      socket.off('return-create-city', resCreateCity);
      socket.off('return-delete-city', resDeleteCity);
    };
  }, [dispatch]);

  const resDeleteCity = (response: any) => {
    if (response.length > 0){
      setFilteredCities((prevItems) => prevItems.filter(item => Number(item.key) !== Number(response[0].id)));
    }
  }

  const resCreateCity = (response: any) => {
    try {
      if (response[0].success === 0) {
        message.error(response[0].message);
        return;
      }
      const adData: any = {
        country: response[0].data.country,
        dataIndex: response[0].data.city,
        duplicate: false,
        key: response[0].data.id,
        similar: false,
        title: response[0].data.city,
      };

      setFilteredCities(prev => [adData, ...prev]);
      setCities(prev => [adData, ...prev]);
      setIsOpenRefCity(false);
    } catch (e) {
      console.log(e);
    }
  };

  const resSearchCountry = (response: any) => {
    try {
      setOptionCountry(response);
    } catch (e) {
      console.log(e);
    }
  };

  const resSearchCity = (response: any) => {
    try {
      setOptionCities(response);
    } catch (e) {
      console.log(e);
    }
  };

  const handleCity = (response: City[]) => {
    setCities(response);
    setFilteredCities(response);
    const hasDuplicate = response.some(item => item.duplicate);
    sessionStorage.setItem('duplicate', String(hasDuplicate));
  };

  const handleCityUpdate = (response: City[]) => {
    setFilteredCities([]);
    setFilteredCities(response);
  };

  const handleDelete = (key: string, title: string) => {
    setCities(prevCities => prevCities.filter(city => city.key !== key));
    setArCity((prev: string[]) => {
      const updatedCities = [...prev, title];
      sessionStorage.setItem('city', JSON.stringify(updatedCities));
      return updatedCities;
    });
    dispatch(deleteCity({key: key, locale: langs}));
  };

  const handleEdit = useCallback((city: City) => {
    setEditingCityKey(city.key);
    setInputValue(city.title);
    setInputValueOld(city.title);
    setArCity((prev: string[]) => [...prev, city.title]);
  }, [setEditingCityKey, setInputValue, setArCity]);

  const handleSave = useCallback(() => {
    setEditingCityKey(null);
    dispatch(updateCity({ id: editingCityKey, text: inputValue, old: inputValueOld, locale: langs }));
  }, [inputValue]);

  const handleCancel = useCallback(() => {
    setEditingCityKey(null);
  }, []);

  const handleSearchCity = (value: string) => {
    const filtered = cities.filter(city =>
      city.title.toLowerCase().includes(value.toLowerCase()) ||
      city.country.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredCities(filtered); // Устанавливаем отфильтрованные города
  };

  const handleCancelRefCity = () => {
    setIsOpenRefCity(false);
  };

  const handleOkRefCity = (data: string) => {
    dispatch(createCity({ data: data, locale: langs }));
  };

  const onSearchCity = (e: string) => {
    if (e.length > 2) {
      dispatch(searchCity({value: e, locale: langs}));
    }
  };

  const onSearchCountry = (e: string) => {
    if (e.length > 2) {
      dispatch(searchCountry({value: e, locale: langs}));
    }
  };

  const handleAddCity = () => {
    setIsOpenRefCity(true);
  };

  return (
    <Layouts>
      <PageContainer style={{ overflow: 'scroll', height: '93vh' }}>
        <Flex vertical gap={10}>
          <Flex gap={10} justify={'space-between'}>
            <Input placeholder={t('CitySearch')} style={{ width: '20%' }}
                   onChange={(e) => handleSearchCity(e.target.value)} />
            <Button type="primary" onClick={handleAddCity}>{t('Add')}</Button>
          </Flex>
          <ProDescriptions actionRef={actionRef} column={3} bordered>
            {filteredCities.map(city => (
              <ProDescriptions.Item key={city.key} dataIndex={city.dataIndex} title={city.country}
                                    style={{ padding: '3px 22px' }}>
                <CityRow
                  key={city.key}
                  city={city}
                  isEditing={editingCityKey === city.key}
                  onEdit={() => handleEdit(city)}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onDelete={() => handleDelete(city.key, city.title)}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                />
              </ProDescriptions.Item>
            ))}
          </ProDescriptions>
        </Flex>
      </PageContainer>
      <Modal open={isOpenRefCity} centered title={t('AddCity')}
             width={700} footer={false} onCancel={handleCancelRefCity}>
        <Form onFinish={handleOkRefCity}>
          <Row justify={'space-between'} gutter={20}>
            <Col span={12} style={{ marginBottom: 10 }}>
              <Form.Item name={'city'} rules={[{ required: true, message: t('RequiredField') }]}>
                <AutoComplete
                  placeholder={t('City')}
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  onSearch={onSearchCity}
                  notFoundContent={null}
                  options={optionCities.map((d: any) => ({ value: d.city, label: d.city }))}
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ marginBottom: 10 }}>
              <Form.Item name={'country'} rules={[{ required: true, message: t('RequiredField') }]}>
                <AutoComplete
                  placeholder={t('Country')}
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  onSearch={onSearchCountry}
                  notFoundContent={null}
                  options={optionCountry.map((d: any) => ({ value: d.country, label: d.country }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={10} justify={'flex-end'}>
            <Button onClick={handleCancelRefCity}>{t('Cancel')}</Button>
            <Button type={'primary'} htmlType={'submit'}>OK</Button>
          </Flex>
        </Form>
      </Modal>
    </Layouts>
  );
};

export default City;